import { buildAppointmentSchedulePlan } from "../scheduling/appointmentSchedulePlan";
import { getAppointmentStaffRequiredTasks } from "../scheduling/appointmentStaffRequiredTasks";
import {
  resolveLocationAppointmentCatalog,
  type LocationAppointmentCatalog,
  type LocationAppointmentCatalogSource,
  type LocationAppointmentProfessional,
} from "@/app/booking/adapters/locationAppointmentCatalog";
import { createLocationServicesAppointmentDraft } from "@/app/booking/adapters/locationServicesAppointmentDraft";
import {
  resolveSpecialistAppointmentSchedulingContext,
  type SpecialistAppointmentSchedulingCatalogSource,
  type SpecialistAppointmentSchedulingContextFailureReason,
  type SpecialistAppointmentSchedulingContextResult,
} from "@/app/booking/adapters/specialistAppointmentSchedulingContext";
import {
  validateAppointmentStepOneBookingDraft,
  type AppointmentBookingDraftV1,
  type AppointmentServiceLineDraft,
} from "@/app/booking/core/appointmentBookingDraft";
import type { SpecialistServiceSelection } from "@/app/specialists/model";
import { getSpecialistWorkplacePresentation } from "@/app/specialists/profilePresentation";

type ResolvedSpecialistAppointmentSchedulingContext = Extract<
  SpecialistAppointmentSchedulingContextResult,
  { ok: true }
>;

export type AppointmentSchedulingContextFailureReason =
  SpecialistAppointmentSchedulingContextFailureReason;

export type AppointmentSchedulingProfessionalPresentation =
  LocationAppointmentProfessional;

export interface AppointmentSchedulingContextSources {
  specialist?: SpecialistAppointmentSchedulingCatalogSource;
  location?: LocationAppointmentCatalogSource;
}

export type AppointmentSchedulingContextResult =
  | {
      ok: true;
      /** Exact current Location route publication; null for Specialist origin. */
      locationAppointmentCatalog: LocationAppointmentCatalog | null;
      scheduling: ResolvedSpecialistAppointmentSchedulingContext["scheduling"];
      presentation: {
        specialist:
          | ResolvedSpecialistAppointmentSchedulingContext["presentation"]["specialist"]
          | null;
        workplace: ResolvedSpecialistAppointmentSchedulingContext["presentation"]["workplace"];
        professionals: readonly AppointmentSchedulingProfessionalPresentation[];
      };
    }
  | {
      ok: false;
      reason: AppointmentSchedulingContextFailureReason;
    };

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasSameStructure(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) {
    return true;
  }

  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((value, index) =>
        hasSameStructure(value, right[index]),
      )
    );
  }

  if (!isRecord(left) || !isRecord(right)) {
    return false;
  }

  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();

  return (
    leftKeys.length === rightKeys.length &&
    leftKeys.every(
      (key, index) =>
        key === rightKeys[index] &&
        hasSameStructure(left[key], right[key]),
    )
  );
}

function getSelectionFromDraftLine(
  line: AppointmentServiceLineDraft,
): SpecialistServiceSelection {
  if (line.selection.kind === "service") {
    return {
      serviceId: line.selection.serviceId,
      variantId: line.selection.variantId,
      addOns: line.selection.addOns.map((addOn) => ({ ...addOn })),
    };
  }

  return {
    serviceId: line.selection.serviceId,
    variantId: null,
    bundleItems: line.selection.bundleItems.flatMap((item) =>
      item.addOns.length > 0
        ? [
            {
              bundleItemId: item.bundleItemId,
              serviceId: item.serviceId,
              variantId: item.variantId,
              addOns: item.addOns.map((addOn) => ({ ...addOn })),
            },
          ]
        : [],
    ),
  };
}

function cloneWorkplaceMarketplacePresentation(
  workplace: ResolvedSpecialistAppointmentSchedulingContext["scheduling"]["workplace"],
) {
  const presentation = getSpecialistWorkplacePresentation(workplace);

  return presentation
    ? {
        ...presentation,
        images: presentation.images?.map((image) => ({ ...image })),
      }
    : null;
}

function resolveLocationSchedulingContext(
  draft: AppointmentBookingDraftV1,
  source?: LocationAppointmentCatalogSource,
): AppointmentSchedulingContextResult {
  const catalog = resolveLocationAppointmentCatalog(
    draft.locationId,
    source,
  );

  if (!catalog) {
    return { ok: false, reason: "catalog-drift" };
  }

  const organizer = draft.participants.find(
    ({ participantId }) => participantId === draft.organizerParticipantId,
  );
  const anchor = catalog.professionals.find(
    ({ userLocationId }) =>
      userLocationId === catalog.catalogAssignment.userLocationId,
  );

  const plan = buildAppointmentSchedulePlan(draft);
  const resourceOnly =
    plan.ok &&
    draft.mode === "individual" &&
    getAppointmentStaffRequiredTasks(plan.plan).length === 0;

  if (!organizer || (!anchor && !resourceOnly)) {
    return { ok: false, reason: "catalog-drift" };
  }

  const selections = [...organizer.serviceLines]
    .sort((left, right) => left.selectionOrder - right.selectionOrder)
    .map(getSelectionFromDraftLine);
  const groupGuests = draft.participants
    .filter(({ participantId }) => participantId !== organizer.participantId)
    .map((participant) => ({
      participantId: participant.participantId,
      selections: [...participant.serviceLines]
        .sort((left, right) => left.selectionOrder - right.selectionOrder)
        .map(getSelectionFromDraftLine),
    }));
  const rebuilt = createLocationServicesAppointmentDraft({
    draftId: draft.draftId,
    mode: draft.mode,
    locationId: draft.locationId,
    catalogAssignment: catalog.catalogAssignment,
    workplace: catalog.workplace,
    services: catalog.services,
    offerings: catalog.offerings,
    selections,
    groupGuests,
    catalogSource: source,
    initialServiceProfessionals: draft.entryPoint.kind === "location-services"
      ? draft.entryPoint.initialServiceProfessionals : undefined,
    initialProfessionalUserLocationId:
      draft.entryPoint.kind === "location-services"
        ? draft.entryPoint.initialProfessionalUserLocationId
        : undefined,
  });

  if (!rebuilt.ok || !hasSameStructure(draft, rebuilt.draft)) {
    return { ok: false, reason: "catalog-drift" };
  }

  return {
    ok: true,
    locationAppointmentCatalog: catalog,
    scheduling: {
      draft: rebuilt.draft,
      assignment: catalog.catalogAssignment,
      workplace: catalog.workplace,
      services: catalog.services,
      offerings: catalog.offerings,
    },
    presentation: {
      specialist: resourceOnly
        ? null
        : {
            personId: catalog.catalogAssignment.personId,
            name: anchor!.name,
            avatarSrc: anchor!.avatarSrc,
            avatarAlt: anchor!.avatarAlt,
            role: anchor!.role,
            category: anchor!.category,
            reviewSummary: { ...anchor!.reviewSummary },
          },
      workplace: {
        locationId: catalog.workplace.locationId,
        name: catalog.workplace.name,
        address: catalog.workplace.address,
        city: catalog.workplace.city,
        state: catalog.workplace.state,
        countryCode: catalog.workplace.countryCode,
        marketplace: cloneWorkplaceMarketplacePresentation(
          catalog.workplace,
        ),
      },
      professionals: resourceOnly
        ? []
        : catalog.professionals.map((professional) => ({
            ...professional,
            reviewSummary: { ...professional.reviewSummary },
          })),
    },
  };
}

/**
 * Revalidates the immutable Step-1 handoff against its exact current catalog
 * before scheduling or completion may consume it.
 */
export function resolveAppointmentSchedulingContext(
  draft: AppointmentBookingDraftV1,
  sources: AppointmentSchedulingContextSources = {},
): AppointmentSchedulingContextResult {
  const validation = validateAppointmentStepOneBookingDraft(draft);

  if (!validation.valid) {
    return { ok: false, reason: "invalid-draft" };
  }

  if (validation.draft.entryPoint.kind === "location-services") {
    return resolveLocationSchedulingContext(
      validation.draft,
      sources.location,
    );
  }

  const specialist = resolveSpecialistAppointmentSchedulingContext(
    validation.draft,
    sources.specialist,
  );

  if (!specialist.ok) {
    return specialist;
  }

  return {
    ...specialist,
    locationAppointmentCatalog: null,
    presentation: {
      ...specialist.presentation,
      professionals: [
        {
          userLocationId: specialist.scheduling.assignment.userLocationId,
          name: specialist.presentation.specialist.name,
          avatarSrc: specialist.presentation.specialist.avatarSrc,
          avatarAlt: specialist.presentation.specialist.avatarAlt,
          role: specialist.presentation.specialist.role,
          category: specialist.presentation.specialist.category,
          reviewSummary: {
            ...specialist.presentation.specialist.reviewSummary,
          },
        },
      ],
    },
  };
}
